import React, { useEffect, useState } from "react";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import moment, { Moment } from "moment";
import { isInclusivelyAfterDay, SingleDatePicker } from "react-dates";
import { ClickAwayListener } from "@mui/material";

import TextInput from "../TextInput/TextInput";

import DatePickerIcon from "./DatePickerIcon";
import MonthSelect from "./MonthSelect";
import YearSelect from "./YearSelect";

interface DatePickerType {
  label: string;
  isRequired: boolean;
  name: string;
  isError: boolean;
  placeholder: string;
  onChange: (date: string) => void;
  onError?: (err: boolean) => void;
  value: string;
  errorMsg?: string;
  maxDate?: string;
  showError?: boolean;
  disabled?: boolean;
  isDob?: boolean;
  isDarkBg?: boolean;
  changeHeight?: boolean;
  preventErase?: boolean;
  futureOnly?: boolean;
  isTransparent?: boolean;
  onlyIconOpens?: boolean;
  /** Input format: "MM/DD/YYYY" (default) or "DD/MM/YYYY" */
  dateFormat?: "MM/DD/YYYY" | "DD/MM/YYYY";
}

/*
 * Date format to be transmitted to the `onChange` handler as the value
 * of the control:
 */
const VALUE_FORMAT = "YYYY-MM-DD";
/*
 * The initial month to display in the date picker:
 */
const INITIAL_VISIBLE_MONTH = moment().subtract(30, "years");
/*
 * The most recent date allowed to be selected:
 */
const MOST_RECENT_ALLOWED_DATE = moment().add(1, "day");

const FORMATTED_DATE_LENGTH_IN_CHARACTERS = 10;

const DatePicker = ({
  label,
  isRequired = false,
  name = "",
  placeholder = "",
  onChange = () => {},
  value = "",
  isError = false,
  onError = () => {},
  errorMsg = "",
  showError = true,
  disabled = false,
  isDob = true,
  isDarkBg = false,
  changeHeight = false,
  preventErase = false,
  futureOnly = false,
  isTransparent = false,
  onlyIconOpens = false,
  dateFormat = "MM/DD/YYYY",
}: DatePickerType) => {
  // Use the dateFormat prop to determine input format
  const INPUT_FORMAT = dateFormat;
  /*
   * Creates a new Moment object from a date string. If the date string
   * is empty, return null. If it is an otherwise invalid date, return today's date.
   */
  const createMoment = (dateString: string) => {
    if (dateString === "") {
      return null;
    }
    return moment(dateString).isValid() ? moment(dateString) : moment();
  };

  /*
   * Whether or not the date picker is currently focused and open:
   */
  const [isFocused, setIsFocused] = useState(false);
  const focus = () => setIsFocused(true);
  const blur = () => setIsFocused(false);
  const toggleFocus = () => setIsFocused(!isFocused);

  /*
   * The Moment version of the string date `value` prop:
   */
  const [momentValue, setMomentValue] = useState<Moment | null>(createMoment(value));

  const [inputValue, setInputValue] = useState<string>(value !== "" ? moment(value).format(INPUT_FORMAT) : "");
  const [isFutureDate, setIsFutureDate] = useState<boolean>(false);
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

  /*
   * Returns which month is visible upon loading the date picker.
   */
  const initialVisibleMonth = isDob ? () => momentValue || INITIAL_VISIBLE_MONTH : null;

  /*
   * Returns whether the date is outside of the appropriate range.
   */
  const isOutsideRange = (date: Moment) => {
    if (futureOnly) {
      return date.isBefore(moment().startOf("day"));
    }
    return isInclusivelyAfterDay(date, MOST_RECENT_ALLOWED_DATE);
  };

  /*
   * Renders the month and year selectors above the calendar.
   */
  const renderMonthElement = ({ month, onMonthSelect, onYearSelect }: any) => {
    const makeHandleChangeSelect = (part: "month" | "year", handler: (date: Moment, value: number) => void) => (date: Moment, value: number) => {
      if (momentValue) {
        const nextValue = momentValue.clone().set({ [part]: value });
        setMomentValue(nextValue);
        onChange(nextValue.format(VALUE_FORMAT));
      }
      handler(date, value);
    };

    return (
      <div style={{ display: "flex", justifyContent: "center" }} onClick={focus}>
        <div style={{ marginRight: "0.5rem" }}>
          <MonthSelect value={month} onChange={makeHandleChangeSelect("month", onMonthSelect)} />
        </div>
        <div>
          <YearSelect value={month} onChange={makeHandleChangeSelect("year", onYearSelect)} futureOnly={futureOnly} />
        </div>
      </div>
    );
  };

  /*
   * Handler for when the user selects a date from the calendar.
   */
  const handleChange = (value: Moment | null) => {
    if (value === null) return;
    setMomentValue(value);
    onChange(value.format(VALUE_FORMAT));
  };

  /*
   * Formats user input for date picker based on the dateFormat.
   * Adds slashes automatically as user types.
   */
  const formatInputValue = (dateStr: string): string => {
    const dateString = dateStr.replaceAll("/", "").replaceAll("-", "").replaceAll(".", "");
    if (dateString.includes(" ") || isNaN(+dateString) || dateString.length > 8) {
      return dateStr.substring(0, dateStr.length - 1);
    }
    const separator = dateFormat === "DD/MM/YYYY" ? "/" : "/";
    if (dateString.length > 4) {
      return dateString.substring(0, 2) + separator + dateString.substring(2, 4) + separator + dateString.substring(4);
    }
    if (dateString.length > 2) {
      return dateString.substring(0, 2) + separator + dateString.substring(2);
    }
    return dateString;
  };

  /*
   * Parses the formatted input string to a moment object based on dateFormat.
   */
  const parseInputToMoment = (formattedInput: string): Moment | null => {
    if (formattedInput.length !== FORMATTED_DATE_LENGTH_IN_CHARACTERS) {
      return null;
    }
    // Parse based on the input format
    const parsed = moment(formattedInput, INPUT_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  };

  /*
   * Handler for when the form text input value changes.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInputValue(e.currentTarget.value);

    if (formatted.length === FORMATTED_DATE_LENGTH_IN_CHARACTERS) {
      blur();
      const parsedMoment = parseInputToMoment(formatted);
      if (parsedMoment) {
        setMomentValue(parsedMoment);
        onChange(parsedMoment.format(VALUE_FORMAT));
      } else {
        onChange("");
      }
    } else {
      onChange("");
    }
    if (formatted.length < FORMATTED_DATE_LENGTH_IN_CHARACTERS) {
      focus();
      onChange("");
      onError(true);
    }
    setInputValue(formatted);
  };

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    } else {
      if (momentValue) {
        const isDateInFuture = momentValue.unix() > moment().unix();
        if (!futureOnly && isDateInFuture) {
          setIsFutureDate(true);
          onChange("");
          onError(true);
        } else {
          setInputValue(momentValue.format(INPUT_FORMAT));
          onChange(momentValue.format(VALUE_FORMAT));
          setIsFutureDate(false);
          onError(false);
        }
      }
    }
  }, [momentValue]);

  /**
   * Whenever the string `value` prop changes, also update the moment object.
   */
  useEffect(() => {
    setMomentValue(createMoment(value));
    if (value === "") {
      setInputValue("");
    }
  }, [value]);

  return (
    <ClickAwayListener onClickAway={blur}>
      <div className={`date relative ${isFocused && changeHeight ? "min-h-[300px]" : ""}`}>
        <TextInput
          onChangeFunc={handleInputChange}
          value={inputValue}
          // onFocus={focus}
          onFocus={onlyIconOpens ? undefined : focus} // Only allow focus if onlyIconOpens is false
          label={label}
          isRequired={isRequired}
          placeholder={placeholder}
          errorMsg={errorMsg}
          disabled={disabled}
          isError={(isError || isFutureDate) && showError}
          onClick={focus}
          isDarkBg={isDarkBg}
          isTransparent={isTransparent}
          readonly={preventErase}
        />
        {!disabled && <DatePickerIcon hasLabel={!!label} onClick={toggleFocus} />}
        <SingleDatePicker
          hideKeyboardShortcutsPanel
          date={momentValue}
          initialVisibleMonth={initialVisibleMonth}
          onDateChange={handleChange}
          focused={isFocused}
          onFocusChange={(arg: { focused: boolean }) => setIsFocused(arg.focused ?? false)}
          id={name}
          numberOfMonths={1}
          renderMonthElement={renderMonthElement}
          onClose={() => setIsFocused(false)}
          isOutsideRange={isOutsideRange}
          placeholder={placeholder}
        />
      </div>
    </ClickAwayListener>
  );
};

export default DatePicker;
